/**
 * MASTER 01 — HERO / Большой эмоциональный разворот.
 *
 * Two exports:
 *   • HeroEditor(block, onPatch, onUpload)  — author-facing editor UI.
 *   • HeroRender(block, mode)               — reader-facing composition (used
 *                                             in Preview shell + reader route).
 *
 * Design DNA (per approved refs):
 *   – premium editorial food publication feel
 *   – warm ivory palette, generous negative space, cinematic photo
 *   – strict controlled tokens (variant / palette / background)
 *   – NO free pixel/font/color/layout controls
 *   – same content drives both desktop and mobile compositions
 */
import { useRef, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Upload, Image as ImageIcon, RotateCcw, Type, Crop } from "lucide-react";
import CropModal from "@/components/editorial/CropModal";

const API_ROOT = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const authHeaders = () => {
  const t = localStorage.getItem("anokina_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// ---------- Controlled design tokens ----------
export const HERO_PALETTES = [
  { key: "ivory",      label: "Warm Ivory",   swatch: "#EFE6D5", accent: "#8A6A1E" },
  { key: "almond",     label: "Almond",       swatch: "#D6B98A", accent: "#8A5A2E" },
  { key: "olive",      label: "Olive",        swatch: "#7E8A3E", accent: "#4B5522" },
  { key: "cocoa",      label: "Cocoa",        swatch: "#5B3B25", accent: "#F1E4CB" },
  { key: "burgundy",   label: "Burgundy",     swatch: "#7A2A2A", accent: "#F1E4CB" },
  { key: "gold",       label: "Antique Gold", swatch: "#C9A84C", accent: "#3B241A" },
  { key: "pistachio",  label: "Pistachio",    swatch: "#A6C088", accent: "#3B241A" },
  { key: "apricot",    label: "Apricot",      swatch: "#E8B48A", accent: "#5B3B25" },
];

export const HERO_BACKGROUNDS = [
  { key: "warm-ivory",       label: "Warm Ivory",       css: "#EFE6D5" },
  { key: "mineral-plaster",  label: "Mineral Plaster",  css: "#E6DFCF" },
  { key: "almond-paper",     label: "Almond Paper",     css: "#F1E7D2" },
  { key: "deep-cocoa",       label: "Deep Cocoa",       css: "#2A1810" },
  { key: "full-image",       label: "Full-bleed image", css: "transparent" },
];

export const HERO_VARIANTS = [
  { key: "A", label: "Фото слева / текст справа" },
  { key: "B", label: "Текст слева / фото справа" },
  { key: "C", label: "Full-bleed фото · текст поверх" },
];

const paletteByKey = (k) => HERO_PALETTES.find((p) => p.key === k) || HERO_PALETTES[0];
const backgroundByKey = (k) => HERO_BACKGROUNDS.find((b) => b.key === k) || HERO_BACKGROUNDS[0];

// ---------- Direct upload helper ----------
async function uploadImageDirect(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await axios.post(`${API_ROOT}/api/admin/media/upload-file`, fd, {
    headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
  });
  return res.data; // { id, file_url, ... }
}

// ---------- Author-facing image slot with «Настроить кадр» ----------
// Exported so other masters (e.g. VisualStoryMaster) can reuse the exact
// same Replace / From Media Library / Crop / Remove controls.
export function ImageSlot({
  label, url, onFile, onClear, onOpenLibrary, onOpenCrop,
  focalX = 0.5, focalY = 0.5, zoom = 1, testid,
}) {
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const m = await uploadImageDirect(f);
      onFile(m);
      toast.success("Фото загружено");
    } catch (err) {
      toast.error("Не удалось загрузить: " + (err?.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      {url ? (
        <>
          <div className="relative w-full aspect-[4/5] rounded-lg overflow-hidden bg-neutral-100"
               data-testid={`${testid}-preview`}>
            <img
              src={url.startsWith("http") ? url : `${API_ROOT}${url}`}
              alt=""
              className="w-full h-full object-cover pointer-events-none"
              style={{
                objectPosition: `${focalX * 100}% ${focalY * 100}%`,
                transform: `scale(${zoom || 1})`,
                transformOrigin: `${focalX * 100}% ${focalY * 100}%`,
              }}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-espresso/25 text-[12px]">
              <input type="file" accept="image/*" className="hidden"
                onChange={handleFile} data-testid={`${testid}-file`} />
              <Upload className="w-3.5 h-3.5" /> {busy ? "Загрузка…" : "Заменить"}
            </label>
            <button type="button" onClick={onOpenLibrary}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-espresso/25 text-[12px]"
              data-testid={`${testid}-library`}>
              <ImageIcon className="w-3.5 h-3.5" /> Из Медиатеки
            </button>
            <button type="button" onClick={onOpenCrop}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-espresso/25 text-[12px]"
              data-testid={`${testid}-crop`}>
              <Crop className="w-3.5 h-3.5" /> Настроить кадр
            </button>
            {onClear && (
              <button type="button" onClick={onClear}
                className="text-[12px] opacity-60 hover:opacity-100"
                data-testid={`${testid}-clear`}>Убрать</button>
            )}
          </div>
        </>
      ) : (
        <div className="rounded-lg bg-cream/80 border border-dashed border-espresso/25 aspect-[4/5] flex flex-col items-center justify-center gap-2 p-4">
          <ImageIcon className="w-6 h-6 opacity-50" />
          <div className="flex flex-wrap gap-2 justify-center">
            <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-espresso text-cream text-[12px]">
              <input type="file" accept="image/*" className="hidden"
                onChange={handleFile} data-testid={`${testid}-file`} />
              <Upload className="w-3.5 h-3.5" /> {busy ? "Загрузка…" : "Загрузить с устройства"}
            </label>
            <button type="button" onClick={onOpenLibrary}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-espresso/25 text-[12px]"
              data-testid={`${testid}-library`}>
              <ImageIcon className="w-3.5 h-3.5" /> Выбрать из Медиатеки
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Media Library picker (uses existing /api/admin/media) ----------
// Exported for reuse from other master editors.
export function MediaLibraryPicker({ open, onClose, onPick }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_ROOT}/api/admin/media`, { headers: authHeaders() });
      setItems((res.data || []).filter((m) => (m.file_url || m.url) && (m.mime || "").startsWith("image/")));
    } catch (e) { toast.error("Не удалось загрузить Медиатеку"); }
    finally { setLoading(false); }
  };

  if (open && items.length === 0 && !loading) load();
  if (!open) return null;

  const filtered = items.filter((m) => !q.trim() ||
    (m.title || m.filename || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4 py-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="hero-media-picker">
      <div className="w-full max-w-4xl max-h-[80vh] overflow-hidden bg-cream rounded-2xl flex flex-col">
        <div className="p-5 border-b border-espresso/10 flex items-center gap-3">
          <div className="font-serif text-xl text-espresso flex-1">Медиатека</div>
          <Input placeholder="Поиск…" value={q} onChange={(e) => setQ(e.target.value)}
            className="max-w-[240px]" />
          <button className="text-sm opacity-70 hover:opacity-100" onClick={onClose}>Закрыть</button>
        </div>
        <div className="p-5 overflow-y-auto grid grid-cols-2 md:grid-cols-4 gap-3">
          {loading && <div className="opacity-60 text-sm">Загрузка…</div>}
          {!loading && filtered.length === 0 && <div className="opacity-60 text-sm">Ничего не найдено</div>}
          {filtered.map((m) => {
            const src = (m.file_url || m.url || "").startsWith("http")
              ? (m.file_url || m.url) : `${API_ROOT}${m.file_url || m.url}`;
            return (
              <button key={m.id} onClick={() => onPick(m)}
                className="text-left group"
                data-testid={`hero-media-item-${m.id}`}>
                <div className="aspect-square rounded-lg overflow-hidden bg-neutral-100">
                  <img src={src} alt="" className="w-full h-full object-cover group-hover:scale-105 transition" />
                </div>
                <div className="text-xs truncate mt-1 opacity-70">{m.title || m.filename}</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ==================================================================
//    HERO EDITOR
// ==================================================================
export function HeroEditor({ block, onPatch }) {
  const c = block.content || {};
  const s = block.settings || {};
  const [libraryFor, setLibraryFor] = useState(null);   // "desktop" | "mobile" | null
  const [cropFor, setCropFor] = useState(null);         // "desktop" | "mobile" | null

  const patchContent = (patch) => onPatch({ content: { ...c, ...patch } });
  const patchSettings = (patch) => onPatch({ settings: { ...s, ...patch } });

  const acceptMedia = (m, slot) => {
    if (slot === "desktop") {
      patchContent({ hero_media_id: m.id, hero_url: m.file_url || m.url });
    } else {
      patchContent({ hero_mobile_media_id: m.id, hero_mobile_url: m.file_url || m.url });
    }
    setLibraryFor(null);
  };

  const saveCrop = ({ focal_x, focal_y, zoom }) => {
    if (cropFor === "desktop") {
      patchSettings({ focal_x, focal_y, hero_zoom: zoom });
    } else {
      patchSettings({ mobile_focal_x: focal_x, mobile_focal_y: focal_y, hero_mobile_zoom: zoom });
    }
    setCropFor(null);
  };

  const variant = s.variant || "A";
  const palette = paletteByKey(s.palette);
  const background = backgroundByKey(s.background);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4" data-testid="hero-editor">
      {/* LEFT: content + images */}
      <div className="rounded-2xl bg-white border hairline p-4 space-y-4">
        <div className="text-[10px] uppercase tracking-widest opacity-70">Содержание</div>
        <div>
          <label className="block text-[11px] opacity-60 mb-1">Надзаголовок</label>
          <Input value={c.eyebrow || ""} onChange={(e) => patchContent({ eyebrow: e.target.value })}
            placeholder="БИСКВИТЫ · 01" data-testid="hero-field-eyebrow" />
        </div>
        <div>
          <label className="block text-[11px] opacity-60 mb-1">Заголовок</label>
          <Input value={c.headline || ""} onChange={(e) => patchContent({ headline: e.target.value })}
            placeholder="Название разворота"
            className="font-serif text-lg" data-testid="hero-field-headline" />
        </div>
        <div>
          <label className="block text-[11px] opacity-60 mb-1">Подзаголовок</label>
          <Input value={c.subtitle || ""} onChange={(e) => patchContent({ subtitle: e.target.value })}
            placeholder="Короткая поясняющая строка" data-testid="hero-field-subtitle" />
        </div>
        <div>
          <label className="block text-[11px] opacity-60 mb-1">Короткий лид</label>
          <textarea value={c.lead || ""} rows={3}
            onChange={(e) => patchContent({ lead: e.target.value })}
            className="w-full border rounded px-2 py-1.5 text-sm italic"
            data-testid="hero-field-lead" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          <ImageSlot
            label="Основное фото · desktop"
            url={c.hero_url}
            focalX={s.focal_x ?? 0.5} focalY={s.focal_y ?? 0.5}
            zoom={s.hero_zoom || 1}
            onFile={(m) => patchContent({ hero_media_id: m.id, hero_url: m.file_url })}
            onClear={() => patchContent({ hero_media_id: null, hero_url: null })}
            onOpenLibrary={() => setLibraryFor("desktop")}
            onOpenCrop={() => setCropFor("desktop")}
            testid="hero-image-desktop" />
          <ImageSlot
            label="Фото для mobile · необязательно"
            url={c.hero_mobile_url}
            focalX={s.mobile_focal_x ?? 0.5} focalY={s.mobile_focal_y ?? 0.5}
            zoom={s.hero_mobile_zoom || 1}
            onFile={(m) => patchContent({ hero_mobile_media_id: m.id, hero_mobile_url: m.file_url })}
            onClear={() => patchContent({ hero_mobile_media_id: null, hero_mobile_url: null })}
            onOpenLibrary={() => setLibraryFor("mobile")}
            onOpenCrop={() => setCropFor("mobile")}
            testid="hero-image-mobile" />
        </div>

        <div className="pt-2">
          <label className="block text-[11px] opacity-60 mb-1">Подпись к фото · необязательно</label>
          <Input value={c.caption || ""} onChange={(e) => patchContent({ caption: e.target.value })}
            data-testid="hero-field-caption" />
        </div>
        <div>
          <label className="block text-[11px] opacity-60 mb-1">Alt-текст · необязательно</label>
          <Input value={c.alt || ""} onChange={(e) => patchContent({ alt: e.target.value })}
            data-testid="hero-field-alt" />
        </div>
      </div>

      {/* RIGHT: composition variant + palette + background + botanical */}
      <div className="rounded-2xl bg-white border hairline p-4 space-y-5">
        <div>
          <div className="text-[10px] uppercase tracking-widest opacity-70 mb-2">Вариант композиции</div>
          <div className="grid grid-cols-3 gap-2">
            {HERO_VARIANTS.map((v) => (
              <button key={v.key} type="button"
                onClick={() => patchSettings({ variant: v.key })}
                className={`p-3 rounded-xl border text-left transition ${
                  variant === v.key ? "border-espresso bg-espresso/5" : "border-espresso/15 hover:bg-cream/60"
                }`}
                data-testid={`hero-variant-${v.key}`}>
                <VariantThumb variant={v.key} />
                <div className="text-[10px] uppercase tracking-widest opacity-70 mt-2">Вариант {v.key}</div>
                <div className="text-[11px] leading-snug mt-1">{v.label}</div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-widest opacity-70 mb-2">Акцентный цвет</div>
          <div className="flex flex-wrap gap-2">
            {HERO_PALETTES.map((p) => (
              <button key={p.key} type="button"
                onClick={() => patchSettings({ palette: p.key })}
                className={`w-8 h-8 rounded-full border-2 transition ${
                  s.palette === p.key ? "border-espresso scale-110" : "border-white shadow"
                }`}
                title={p.label}
                style={{ background: p.swatch }}
                data-testid={`hero-palette-${p.key}`} />
            ))}
          </div>
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-widest opacity-70 mb-1">Фон</label>
          {/* Native <select> — reliable on iOS Safari; no portal, no jump. */}
          <select
            value={s.background || "warm-ivory"}
            onChange={(e) => patchSettings({ background: e.target.value })}
            className="w-full h-10 rounded-md border border-espresso/25 bg-white px-3 text-sm outline-none focus:border-espresso"
            data-testid="hero-background">
            {HERO_BACKGROUNDS.map((b) => (
              <option key={b.key} value={b.key}>{b.label}</option>
            ))}
          </select>
        </div>

        <label className="flex items-center gap-3 pt-1">
          <input type="checkbox" checked={!!s.botanical}
            onChange={(e) => patchSettings({ botanical: e.target.checked })}
            data-testid="hero-botanical" />
          <span className="text-sm">Ботаническая декоративная деталь</span>
        </label>

        <div className="text-[10px] opacity-50 border-t border-espresso/10 pt-3">
          MASTER 01 · большой эмоциональный разворот.
          Автор редактирует один раз — Desktop и Mobile создаются автоматически.
        </div>
      </div>

      <MediaLibraryPicker open={!!libraryFor} onClose={() => setLibraryFor(null)}
        onPick={(m) => acceptMedia(m, libraryFor)} />
      <CropModal
        open={!!cropFor}
        mode={cropFor || "desktop"}
        url={cropFor === "mobile" ? (c.hero_mobile_url || c.hero_url) : c.hero_url}
        focalX={cropFor === "mobile" ? (s.mobile_focal_x ?? s.focal_x ?? 0.5) : (s.focal_x ?? 0.5)}
        focalY={cropFor === "mobile" ? (s.mobile_focal_y ?? s.focal_y ?? 0.5) : (s.focal_y ?? 0.5)}
        zoom={cropFor === "mobile" ? (s.hero_mobile_zoom || 1) : (s.hero_zoom || 1)}
        onSave={saveCrop}
        onClose={() => setCropFor(null)}
      />
    </div>
  );
}

// Small variant thumbnail
function VariantThumb({ variant }) {
  const p = "bg-espresso/20 rounded";
  const t = "bg-espresso/40 rounded h-1";
  if (variant === "A") return (
    <div className="grid grid-cols-[3fr_2fr] gap-1.5 h-14">
      <div className={p} />
      <div className="flex flex-col justify-center gap-1"><div className={`${t} w-3/4`} /><div className={`${t} w-full`} /><div className={`${t} w-2/3`} /></div>
    </div>
  );
  if (variant === "B") return (
    <div className="grid grid-cols-[2fr_3fr] gap-1.5 h-14">
      <div className="flex flex-col justify-center gap-1"><div className={`${t} w-3/4`} /><div className={`${t} w-full`} /><div className={`${t} w-2/3`} /></div>
      <div className={p} />
    </div>
  );
  return (
    <div className="relative h-14">
      <div className={`${p} absolute inset-0`} />
      <div className="absolute inset-0 flex flex-col justify-end p-1 gap-0.5">
        <div className="bg-white/70 h-1 w-3/4 rounded" />
        <div className="bg-white/70 h-1 w-2/3 rounded" />
      </div>
    </div>
  );
}

// ==================================================================
//   HERO RENDER — reader-facing final composition
// ==================================================================
export function HeroRender({ block, mode = "desktop" }) {
  const c = block.content || {};
  const s = block.settings || {};
  const variant = s.variant || "A";
  const palette = paletteByKey(s.palette);
  const background = backgroundByKey(s.background);
  const useFullBleed = variant === "C" || s.background === "full-image";

  const desktopSrc = (c.hero_url || "").startsWith("http") ? c.hero_url : `${API_ROOT}${c.hero_url || ""}`;
  const mobileSrc = c.hero_mobile_url
    ? ((c.hero_mobile_url || "").startsWith("http") ? c.hero_mobile_url : `${API_ROOT}${c.hero_mobile_url}`)
    : desktopSrc;

  const fx = (s.focal_x ?? 0.5) * 100;
  const fy = (s.focal_y ?? 0.5) * 100;
  const mfx = (s.mobile_focal_x ?? s.focal_x ?? 0.5) * 100;
  const mfy = (s.mobile_focal_y ?? s.focal_y ?? 0.5) * 100;
  const dzoom = s.hero_zoom || 1;
  const mzoom = s.hero_mobile_zoom || 1;

  const bg = background.css === "transparent" ? "#EFE6D5" : background.css;
  const brown = "#3B241A";
  const textOnDark = background.key === "deep-cocoa";
  const primaryText = textOnDark ? "#F1E4CB" : brown;

  // ---------- MOBILE (~393px) — intentional vertical premium magazine opening ----------
  if (mode === "mobile") {
    return (
      <section
        className="relative w-full overflow-hidden"
        style={{ background: bg }}
        data-testid="hero-render-mobile"
        data-variant={variant}>
        {/* portrait cinematic image */}
        <div className="relative aspect-[3/4] w-full">
          {desktopSrc && (
            <img src={mobileSrc} alt={c.alt || ""}
              className="absolute inset-0 w-full h-full object-cover"
              style={{
                objectPosition: `${mfx}% ${mfy}%`,
                transform: `scale(${mzoom})`,
                transformOrigin: `${mfx}% ${mfy}%`,
              }} />
          )}
          {c.eyebrow && (
            <div className="absolute top-6 left-6 text-[10px] tracking-[0.42em] uppercase"
                 style={{ color: "#F5EAD8" }}>
              {c.eyebrow}
            </div>
          )}
        </div>
        <div className="px-6 pt-7 pb-10 -mt-16 relative">
          {/* soft transition */}
          <div className="absolute inset-x-0 top-0 h-16 pointer-events-none"
               style={{ background: `linear-gradient(180deg, ${bg}00 0%, ${bg} 100%)` }} />
          <h1
            className="font-serif leading-[0.95] relative"
            style={{
              color: primaryText,
              fontFamily: "'Cormorant Garamond', 'Playfair Display', serif",
              fontSize: "clamp(40px, 12vw, 56px)",
            }}
            data-testid="hero-render-headline">
            {c.headline || "Заголовок"}
          </h1>
          {/* decorative divider using palette accent */}
          <div className="mt-4 flex items-center gap-2">
            <span className="h-px w-8 block" style={{ background: palette.swatch }} />
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: palette.swatch }} />
          </div>
          {c.subtitle && (
            <div className="italic text-[17px] mt-3 leading-snug"
                 style={{ color: primaryText, opacity: 0.85 }}>
              {c.subtitle}
            </div>
          )}
          {c.lead && (
            <p className="mt-5 text-[15px] leading-[1.75]"
               style={{ color: primaryText, opacity: 0.9 }}>
              {c.lead}
            </p>
          )}
          {c.caption && (
            <div className="mt-6 text-[11px] italic opacity-60"
                 style={{ color: primaryText }}>
              {c.caption}
            </div>
          )}
        </div>
      </section>
    );
  }

  // ---------- DESKTOP — cinematic asymmetric composition ----------
  const TextColumn = (
    <div className="relative px-8 lg:px-14 xl:px-16 py-14 lg:py-20 flex flex-col justify-center"
      style={{ color: primaryText }}>
      {c.eyebrow && (
        <div className="text-[11px] tracking-[0.48em] uppercase mb-6 opacity-80"
             data-testid="hero-render-eyebrow">
          {c.eyebrow}
        </div>
      )}
      <h1
        className="font-serif leading-[0.9]"
        style={{
          fontFamily: "'Cormorant Garamond', 'Playfair Display', serif",
          fontSize: "clamp(64px, 7vw, 128px)",
          color: primaryText,
        }}
        data-testid="hero-render-headline">
        {c.headline || "Заголовок"}
      </h1>
      <div className="mt-6 flex items-center gap-3">
        <span className="h-px w-12 block" style={{ background: palette.swatch }} />
        <span className="h-2 w-2 rounded-full" style={{ background: palette.swatch }} />
        <span className="h-px w-4 block" style={{ background: palette.swatch, opacity: 0.6 }} />
      </div>
      {c.subtitle && (
        <div className="italic text-[22px] mt-6 leading-tight max-w-md"
             style={{ opacity: 0.9 }}>
          {c.subtitle}
        </div>
      )}
      {c.lead && (
        <p className="mt-10 text-[16px] leading-[1.9] max-w-[380px]"
           style={{ opacity: 0.9 }}>
          {c.lead}
        </p>
      )}
      {c.caption && (
        <div className="mt-10 text-[11px] italic opacity-60 max-w-md">{c.caption}</div>
      )}
    </div>
  );

  const ImageColumn = (
    <div className="relative overflow-hidden" style={{ minHeight: 520 }}>
      {desktopSrc && (
        <img src={desktopSrc} alt={c.alt || ""}
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            objectPosition: `${fx}% ${fy}%`,
            transform: `scale(${dzoom})`,
            transformOrigin: `${fx}% ${fy}%`,
          }} />
      )}
      {s.botanical && (
        // subtle decorative accent — kept extremely restrained
        <div className="absolute -left-6 bottom-6 w-40 h-40 rounded-full opacity-20"
             style={{ background: `radial-gradient(circle at 30% 30%, ${palette.swatch}, transparent 60%)` }} />
      )}
    </div>
  );

  // Variant C: full-bleed image with text integrated inside safe zone
  if (variant === "C") {
    return (
      <section className="relative w-full overflow-hidden"
        style={{ background: bg, minHeight: 640 }}
        data-testid="hero-render-desktop" data-variant="C">
        <div className="absolute inset-0">{ImageColumn}</div>
        <div className="absolute inset-0 bg-gradient-to-r from-[rgba(20,10,4,0.55)] via-[rgba(20,10,4,0.2)] to-transparent" />
        <div className="relative z-10 max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-2">
          <div className="p-10 lg:p-16 xl:p-20 text-[#F5EAD8]" style={{ color: "#F5EAD8" }}>
            {c.eyebrow && <div className="text-[11px] tracking-[0.48em] uppercase mb-6 opacity-90">{c.eyebrow}</div>}
            <h1 className="font-serif leading-[0.9]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(60px, 7vw, 120px)" }}
              data-testid="hero-render-headline">
              {c.headline || "Заголовок"}
            </h1>
            {c.subtitle && <div className="italic text-[22px] mt-6 max-w-md opacity-90">{c.subtitle}</div>}
            {c.lead && <p className="mt-8 text-[15px] leading-[1.85] max-w-[380px] opacity-90">{c.lead}</p>}
          </div>
        </div>
      </section>
    );
  }

  // Variants A (photo left / text right) and B (text left / photo right)
  return (
    <section
      className="relative w-full grid grid-cols-1 lg:grid-cols-2"
      style={{ background: bg, minHeight: 640 }}
      data-testid="hero-render-desktop" data-variant={variant}>
      {variant === "A" ? (
        <>
          <div className="lg:order-1">{ImageColumn}</div>
          <div className="lg:order-2">{TextColumn}</div>
        </>
      ) : (
        <>
          <div className="lg:order-1">{TextColumn}</div>
          <div className="lg:order-2">{ImageColumn}</div>
        </>
      )}
    </section>
  );
}
